import { useState, useEffect } from "react";
import { useBusinesses } from "@/hooks/use-businesses";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusCircle, Pencil, Trash2, Check, AlertTriangle, BookOpen, Loader2, Download } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

// Types
type Account = {
  id: number;
  chart_id: number;
  code: string;
  name: string;
  type: string;
  description?: string;
  balance?: string;
  parent_id?: number | null;
  is_active?: boolean;
};

type AccountEditFormData = {
  code: string;
  name: string;
  type: string;
  description: string;
  parent_id?: number | null;
};

export default function DirectAccountsPage() {
  const { activeBusiness } = useBusinesses();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [formData, setFormData] = useState<AccountEditFormData>({
    code: "",
    name: "",
    type: "asset",
    description: "",
    parent_id: null
  });

  // State for accounts and chart data
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [chart, setChart] = useState<{ id: number; name: string } | null>(null);

  // COA import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [templates, setTemplates] = useState<Array<{ id: number; name: string; industry: string; description: string | null }>>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [templatePreview, setTemplatePreview] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importConfirmed, setImportConfirmed] = useState(false);

  // Fetch templates when modal opens
  useEffect(() => {
    if (showImportModal) {
      apiRequest("GET", "/api/coa/templates").then(r => r.json()).then(setTemplates).catch(() => {});
    }
  }, [showImportModal]);

  // Fetch template preview when selected
  useEffect(() => {
    if (selectedTemplateId) {
      setPreviewLoading(true);
      setImportConfirmed(false);
      apiRequest("GET", `/api/coa/templates/${selectedTemplateId}`).then(r => r.json()).then(d => { setTemplatePreview(d); setPreviewLoading(false); }).catch(() => setPreviewLoading(false));
    } else {
      setTemplatePreview(null);
    }
  }, [selectedTemplateId]);

  // Import handler
  const handleImport = async () => {
    if (!activeBusiness?.id || !selectedTemplateId) return;
    setImporting(true);
    try {
      const res = await fetch("/api/coa/import-from-template", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ templateId: selectedTemplateId }),
      });
      const data = await res.json();

      if (!res.ok) {
        if (res.status === 409) {
          toast({ title: "Already Imported", description: "Chart of Accounts was already set up for this business.", variant: "destructive" });
        } else {
          toast({ title: "Import failed", description: data.message || "Something went wrong", variant: "destructive" });
        }
        setShowImportModal(false);
        return;
      }

      toast({ title: "Chart of Accounts imported", description: `${data.accountsCreated} accounts from "${data.templateName}"` });
      setShowImportModal(false);
      window.location.reload();
    } catch (err: any) {
      toast({ title: "Import failed", description: err.message || "Something went wrong", variant: "destructive" });
    } finally {
      setImporting(false);
    }
  };

  // Load accounts from API
  useEffect(() => {
    if (activeBusiness?.id) {
      setLoading(true);

      fetch(`/api/businesses/${activeBusiness.id}/chart-of-accounts`, { credentials: "include" })
        .then(response => response.json())
        .then(data => {
          // New format: { initialized, accounts[] }
          if (data && data.accounts && Array.isArray(data.accounts)) {
            const formattedAccounts = data.accounts.map((account: any) => ({
              id: account.id,
              chart_id: activeBusiness.id,
              code: account.code || "0000",
              name: account.name,
              type: account.type || "asset",
              description: account.description || account.subtype || "",
              balance: account.balance || "0.00",
              parent_id: account.parentId || null,
              is_active: account.isActive !== false
            }));
            setAccounts(formattedAccounts);
            setChart({ id: activeBusiness.id, name: `${activeBusiness.name} Chart of Accounts` });
          } else {
            setAccounts([]);
            setChart({ id: activeBusiness.id, name: `${activeBusiness.name} Chart of Accounts` });
          }
          setLoading(false);
        })
        .catch(error => {
          console.error("Error fetching chart data:", error);
          setAccounts([]);
          setLoading(false);
        });
    }
  }, [activeBusiness?.id]);

  // Filter accounts based on active tab
  const filteredAccounts = accounts.filter(account => {
    if (activeTab === "all") return true;
    return account.type === activeTab;
  });

  // Sort accounts by code
  const sortedAccounts = filteredAccounts.sort((a, b) => {
    return a.code.localeCompare(b.code);
  });

  const openEditDialog = (account: Account) => {
    setSelectedAccount(account);
    setFormData({
      code: account.code,
      name: account.name,
      type: account.type,
      description: account.description || "",
      parent_id: account.parent_id
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (account: Account) => {
    setSelectedAccount(account);
    setShowDeleteConfirm(true);
  };

  const handleAddAccount = () => {
    const newAccount: Account = {
      id: Math.max(...accounts.map(a => a.id), 0) + 1,
      chart_id: chart?.id || 1,
      code: formData.code,
      name: formData.name,
      type: formData.type,
      description: formData.description,
      parent_id: formData.parent_id,
      balance: "0.00",
      is_active: true
    };

    setAccounts([...accounts, newAccount]);
    setShowAddDialog(false);
    toast({
      title: "Success",
      description: "Account added successfully",
    });
    
    setFormData({
      code: "",
      name: "",
      type: "asset",
      description: "",
      parent_id: null
    });
  };

  const handleEditAccount = () => {
    if (!selectedAccount) return;

    const updatedAccounts = accounts.map(account => 
      account.id === selectedAccount.id 
        ? {
            ...account,
            code: formData.code,
            name: formData.name,
            type: formData.type,
            description: formData.description,
            parent_id: formData.parent_id
          }
        : account
    );

    setAccounts(updatedAccounts);
    setShowEditDialog(false);
    toast({
      title: "Success", 
      description: "Account updated successfully",
    });
  };

  const handleDeleteAccount = () => {
    if (!selectedAccount) return;

    const updatedAccounts = accounts.filter(account => account.id !== selectedAccount.id);
    setAccounts(updatedAccounts);
    setShowDeleteConfirm(false);
    toast({
      title: "Success",
      description: "Account deleted successfully",
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto p-6">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto p-6">
        <div className="mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Chart of Accounts</h1>
              <p className="text-gray-600 mt-1">
                {chart?.name || `${activeBusiness?.name} Chart of Accounts`}
              </p>
            </div>
            <Button onClick={() => setShowAddDialog(true)} className="flex items-center gap-2">
              <PlusCircle className="h-4 w-4" />
              Add Account
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Accounts</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-6">
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="asset">Assets</TabsTrigger>
                <TabsTrigger value="liability">Liabilities</TabsTrigger>
                <TabsTrigger value="equity">Equity</TabsTrigger>
                <TabsTrigger value="income">Income</TabsTrigger>
                <TabsTrigger value="expense">Expenses</TabsTrigger>
              </TabsList>
              
              <TabsContent value={activeTab} className="mt-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Account Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Balance</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedAccounts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-12">
                          <div className="flex flex-col items-center gap-4 max-w-md mx-auto">
                            <BookOpen className="h-12 w-12 text-blue-400" />
                            <h3 className="text-lg font-semibold text-gray-900">Setup Your Chart of Accounts</h3>
                            <p className="text-sm text-gray-500 text-center">
                              Import a prebuilt industry-specific chart or create accounts manually.
                            </p>
                            <div className="flex gap-3">
                              <Button onClick={() => setShowImportModal(true)} className="gap-2">
                                <Download className="h-4 w-4" /> Import Chart of Accounts
                              </Button>
                              <Button variant="outline" onClick={() => setShowAddDialog(true)} className="gap-2">
                                <PlusCircle className="h-4 w-4" /> Create Manually
                              </Button>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedAccounts.map((account) => (
                        <TableRow key={account.id}>
                          <TableCell className="font-mono">{account.code}</TableCell>
                          <TableCell className="font-medium">{account.name}</TableCell>
                          <TableCell>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                              {account.type}
                            </span>
                          </TableCell>
                          <TableCell>{account.description || "-"}</TableCell>
                          <TableCell className="font-mono">${account.balance || "0.00"}</TableCell>
                          <TableCell>
                            {account.is_active ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <Check className="w-3 h-3 mr-1" />
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                Inactive
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(account)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDeleteDialog(account)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Add Account Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Account</DialogTitle>
              <DialogDescription>
                Create a new account in your chart of accounts.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="code" className="text-right">Code</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value})}
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="type" className="text-right">Type</Label>
                <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
                  <SelectTrigger className="col-span-3">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asset">Asset</SelectItem>
                    <SelectItem value="liability">Liability</SelectItem>
                    <SelectItem value="equity">Equity</SelectItem>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="description" className="text-right">Description</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="col-span-3"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
              <Button onClick={handleAddAccount}>Add Account</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Account Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Account</DialogTitle>
              <DialogDescription>
                Update the account information.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-code" className="text-right">Code</Label>
                <Input
                  id="edit-code"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value})}
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-name" className="text-right">Name</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-type" className="text-right">Type</Label>
                <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
                  <SelectTrigger className="col-span-3">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asset">Asset</SelectItem>
                    <SelectItem value="liability">Liability</SelectItem>
                    <SelectItem value="equity">Equity</SelectItem>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-description" className="text-right">Description</Label>
                <Input
                  id="edit-description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="col-span-3"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button onClick={handleEditAccount}>Update Account</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete the account "{selectedAccount?.name}". 
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteAccount}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* COA Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import Chart of Accounts</DialogTitle>
            <DialogDescription>Select an industry template to import a prebuilt chart of accounts.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Template selector */}
            <div className="space-y-2">
              <Label>Industry Template</Label>
              <Select value={selectedTemplateId?.toString() || ""} onValueChange={(v) => { setSelectedTemplateId(parseInt(v)); setImportConfirmed(false); }}>
                <SelectTrigger><SelectValue placeholder="Choose a template..." /></SelectTrigger>
                <SelectContent>
                  {templates.map((t: any) => (
                    <SelectItem key={t.id} value={t.id.toString()}>
                      {t.name} ({t.accountCount || 0} accounts)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Preview */}
            {selectedTemplateId && (
              <div className="border rounded-lg max-h-48 overflow-y-auto">
                {previewLoading ? (
                  <div className="p-4 flex items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading preview...
                  </div>
                ) : templatePreview?.accounts ? (
                  <div className="divide-y">
                    {['Asset', 'Liability', 'Equity', 'Income', 'Expense'].map((type) => {
                      const accts = templatePreview.accounts.filter((a: any) => a.type.toLowerCase() === type.toLowerCase());
                      if (accts.length === 0) return null;
                      return (
                        <div key={type} className="p-2">
                          <div className="text-xs font-semibold text-muted-foreground uppercase mb-1">{type} ({accts.length})</div>
                          {accts.map((a: any) => (
                            <div key={a.code} className="flex gap-2 py-0.5 text-sm">
                              <span className="font-mono text-xs text-muted-foreground w-10">{a.code}</span>
                              <span>{a.name}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            )}

            {/* Confirmation */}
            {selectedTemplateId && (
              <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <Checkbox id="importConfirm" checked={importConfirmed} onCheckedChange={(c) => setImportConfirmed(c === true)} />
                <label htmlFor="importConfirm" className="text-sm cursor-pointer">
                  I understand this can only be done once and confirm the import
                </label>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportModal(false)}>Cancel</Button>
            <Button onClick={handleImport} disabled={!importConfirmed || !selectedTemplateId || importing} className="gap-2">
              {importing && <Loader2 className="h-4 w-4 animate-spin" />}
              Import {templatePreview?.accounts?.length || 0} Accounts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}